import { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { MONTH_LABELS, formatCompactINR, formatNumber, monthLabel } from "@/lib/cibil/format";
import {
  accountsByType,
  dpdAccountsByType,
  dpdBifurcation,
  dpdDaysHeatmap,
  repaymentHistory,
  repaymentsOverview,
} from "@/lib/cibil/metrics";
import type { NormalizedReport } from "@/lib/cibil/normalize";

const AXIS = { fontSize: 11, fill: "hsl(var(--muted-foreground))" };
const GRID = { strokeDasharray: "2 6", stroke: "hsl(var(--border))", strokeOpacity: 0.7 };
const CURSOR = { fill: "hsl(var(--muted))", fillOpacity: 0.35, radius: 8 };
const ANIM = { animationDuration: 700, animationEasing: "ease-out" as const };

/** Severity ramp for delay buckets: mild delays green/amber, deep delays dark red. */
const BUCKET_COLORS: Record<string, string> = {
  lt30: "oklch(0.66 0.15 145)",
  "30_60": "oklch(0.78 0.15 85)",
  "60_90": "oklch(0.68 0.17 45)",
  "90_180": "oklch(0.58 0.21 22)",
  gte180: "oklch(0.42 0.17 20)",
  all: "var(--chart-1)",
};

const money = (v: number) => formatCompactINR(v);
const count = (v: number) => formatNumber(v);

/** Vertical soft-to-solid gradient fill used by every bar/area series. */
function SeriesGradient({ id, color }: { id: string; color: string }) {
  return (
    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stopColor={color} stopOpacity={0.95} />
      <stop offset="100%" stopColor={color} stopOpacity={0.45} />
    </linearGradient>
  );
}

interface SeriesMeta {
  key: string;
  name: string;
  color: string;
}

function LegendChips({ series }: { series: SeriesMeta[] }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      {series.map((s) => (
        <span key={s.key} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
          <span className="size-2.5 rounded-full" style={{ background: s.color }} />
          {s.name}
        </span>
      ))}
    </div>
  );
}

function ChartTooltip({
  active,
  payload,
  label,
  labelFormat,
  valueFormat,
  showTotal,
}: {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  active?: boolean;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  payload?: any[];
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  label?: any;
  labelFormat?: (v: string) => string;
  valueFormat: (v: number) => string;
  showTotal?: boolean;
}) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((p) => p.value !== null && p.value !== undefined);
  const total = rows.reduce((a, p) => a + Number(p.value ?? 0), 0);
  return (
    <div className="min-w-44 rounded-xl border border-border bg-popover/95 p-3 shadow-lg backdrop-blur">
      <p className="mb-2 text-xs font-medium text-muted-foreground">
        {labelFormat ? labelFormat(String(label)) : String(label)}
      </p>
      <div className="grid gap-1.5">
        {rows.map((p) => (
          <div key={p.dataKey} className="flex items-center justify-between gap-4 text-xs">
            <span className="inline-flex items-center gap-1.5">
              <span className="size-2 rounded-full" style={{ background: p.color ?? p.fill }} />
              {p.name}
            </span>
            <span className="font-medium tabular-nums">{valueFormat(Number(p.value))}</span>
          </div>
        ))}
        {showTotal && rows.length > 1 ? (
          <div className="mt-1 flex items-center justify-between gap-4 border-t border-border pt-1.5 text-xs font-semibold">
            <span>Total</span>
            <span className="tabular-nums">{valueFormat(total)}</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function ChartCard({
  title,
  description,
  series,
  empty,
  children,
  actions,
  minWidth = 640,
}: {
  title: string;
  description?: string;
  series?: SeriesMeta[];
  empty?: boolean;
  children: React.ReactNode;
  actions?: React.ReactNode;
  minWidth?: number;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <CardTitle className="font-display text-base">{title}</CardTitle>
            {description ? <CardDescription>{description}</CardDescription> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
        {series?.length ? (
          <div className="pt-2">
            <LegendChips series={series} />
          </div>
        ) : null}
      </CardHeader>
      <CardContent>
        {empty ? (
          <div className="flex h-56 items-center justify-center rounded-xl bg-muted/40 text-sm text-muted-foreground">
            No data reported for this view.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div style={{ minWidth }} className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                {children as React.ReactElement}
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function ChartView({ report }: { report: NormalizedReport }) {
  const accounts = report.accounts;
  const [range, setRange] = useState(24);
  const [mode, setMode] = useState<"amount" | "count">("amount");
  const anchor = useMemo(
    () =>
      report.generatedAt ??
      accounts.reduce<Date | null>(
        (max, a) => (a.lastReported && (!max || a.lastReported > max) ? a.lastReported : max),
        null,
      ) ??
      new Date(),
    [report.generatedAt, accounts],
  );
  const history = useMemo(
    () => repaymentHistory(accounts, range, anchor),
    [accounts, range, anchor],
  );
  const overview = useMemo(() => repaymentsOverview(accounts), [accounts]);
  const byType = useMemo(() => dpdAccountsByType(accounts), [accounts]);
  const buckets = useMemo(() => dpdBifurcation(accounts), [accounts]);
  const severity = useMemo(
    () =>
      buckets
        .filter((b) => b.bucket !== "all")
        .map((b) => ({
          label: b.label,
          total: b.total,
          color: BUCKET_COLORS[b.bucket] ?? "var(--chart-3)",
        }))
        .reverse(),
    [buckets],
  );
  const typeMix = useMemo(() => accountsByType(report), [report]);

  const heatRows = useMemo(() => dpdDaysHeatmap(accounts, anchor), [accounts, anchor]);
  const maxCell = Math.max(1, ...heatRows.flatMap((y) => y.months));
  /** Heatmap colour ramp driven purely by how many DPDs fell in that month. */
  const heatColor = (c: number) => {
    const t = c / maxCell;
    if (t <= 0.2) return "oklch(0.66 0.15 145)";
    if (t <= 0.4) return "oklch(0.78 0.15 85)";
    if (t <= 0.6) return "oklch(0.68 0.17 45)";
    if (t <= 0.8) return "oklch(0.58 0.21 22)";
    return "oklch(0.42 0.17 20)";
  };


  const repaymentSeries: SeriesMeta[] = [
    { key: "onTime", name: "On-time", color: "var(--chart-2)" },
    { key: "delayed", name: "Delayed", color: "var(--chart-3)" },
  ];
  const dpdSeries: SeriesMeta[] = [
    { key: "total", name: "Total accounts", color: "var(--chart-1)" },
    { key: "withDelays", name: "With delays", color: "var(--chart-3)" },
  ];
  const mixSeries: SeriesMeta[] = [
    { key: "enquiries", name: "Enquiries", color: "var(--chart-4)" },
    { key: "accounts", name: "Approved accounts", color: "var(--chart-1)" },
  ];

  return (
    <div className="grid gap-4">
      <ChartCard
        title="Repayments History of All Loans"
        description={
          mode === "amount"
            ? "Rolling monthly window ending on the report date. Amount prefers reported EMI; when EMI is missing, outstanding / tenure / last payment is used as a proxy."
            : "Rolling monthly window ending on the report date. Count of accounts reporting on-time vs delayed in each month."
        }
        series={repaymentSeries}
        empty={history.every(
          (h) =>
            h.onTimeCount + h.delayedCount === 0 &&
            h.onTime + h.delayed === 0
        )}
        actions={
          <>
            <div className="flex items-center gap-1 rounded-lg bg-muted p-0.5">
              {[12, 24, 36].map((r) => (
                <Button
                  key={r}
                  size="sm"
                  variant={range === r ? "default" : "ghost"}
                  className="h-7 px-2.5 text-xs"
                  onClick={() => setRange(r)}
                >
                  {r}m
                </Button>
              ))}
            </div>
            <div className="flex items-center gap-1 rounded-lg bg-muted p-0.5">
              {(["amount", "count"] as const).map((m) => (
                <Button
                  key={m}
                  size="sm"
                  variant={mode === m ? "default" : "ghost"}
                  className="h-7 px-2.5 text-xs capitalize"
                  onClick={() => setMode(m)}
                >
                  {m}
                </Button>
              ))}
            </div>
          </>
        }
      >
        <BarChart data={history} barCategoryGap="28%" margin={{ top: 8, right: 8 }}>
          <defs>
            <SeriesGradient id="grad-ontime" color="var(--chart-2)" />
            <SeriesGradient id="grad-delayed" color="var(--chart-3)" />
          </defs>
          <CartesianGrid {...GRID} vertical={false} />
          <XAxis dataKey="month" tickFormatter={monthLabel} tick={AXIS} tickLine={false} axisLine={false} tickMargin={8} />
          <YAxis
            tickFormatter={mode === "amount" ? money : count}
            tick={AXIS}
            tickLine={false}
            axisLine={false}
            width={70}
            allowDecimals={false}
          />
          <RTooltip
            cursor={CURSOR}
            content={
              <ChartTooltip
                valueFormat={mode === "amount" ? money : count}
                labelFormat={monthLabel}
                showTotal
              />
            }
          />
          <Bar
            dataKey={mode === "amount" ? "onTime" : "onTimeCount"}
            name="On-time"
            stackId="a"
            fill="url(#grad-ontime)"
            maxBarSize={26}
            {...ANIM}
          />
          <Bar
            dataKey={mode === "amount" ? "delayed" : "delayedCount"}
            name="Delayed"
            stackId="a"
            fill="url(#grad-delayed)"
            radius={[6, 6, 0, 0]}
            maxBarSize={26}
            {...ANIM}
          />
        </BarChart>
      </ChartCard>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard title="Repayments Overview" minWidth={420} empty={overview.length === 0}>
          <BarChart data={overview} layout="vertical" margin={{ left: 40, right: 16 }} barCategoryGap="30%">
            <defs>
              {overview.map((o, i) => (
                <SeriesGradient key={o.label} id={`grad-ov-${i}`} color={`var(--chart-${(i % 5) + 1})`} />
              ))}
            </defs>
            <CartesianGrid {...GRID} horizontal={false} />
            <XAxis type="number" tickFormatter={money} tick={AXIS} tickLine={false} axisLine={false} />
            <YAxis type="category" dataKey="label" tick={AXIS} width={130} tickLine={false} axisLine={false} />
            <RTooltip cursor={CURSOR} content={<ChartTooltip valueFormat={money} />} />
            <Bar dataKey="value" name="Amount" radius={[0, 8, 8, 0]} maxBarSize={26} {...ANIM}>
              {overview.map((o, i) => (
                <Cell key={o.label} fill={`url(#grad-ov-${i})`} />
              ))}
            </Bar>
          </BarChart>
        </ChartCard>

        <ChartCard title="DPD Accounts by Type" series={dpdSeries} minWidth={420} empty={byType.length === 0}>
          <BarChart data={byType} barCategoryGap="26%" barGap={4} margin={{ top: 8 }}>
            <defs>
              <SeriesGradient id="grad-total" color="var(--chart-1)" />
              <SeriesGradient id="grad-withdelays" color="var(--chart-3)" />
            </defs>
            <CartesianGrid {...GRID} vertical={false} />
            <XAxis dataKey="type" tick={AXIS} tickLine={false} axisLine={false} interval={0} height={56} angle={-15} textAnchor="end" />
            <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} width={40} />
            <RTooltip cursor={CURSOR} content={<ChartTooltip valueFormat={count} />} />
            <Bar dataKey="total" name="Total accounts" fill="url(#grad-total)" radius={[6, 6, 0, 0]} maxBarSize={28} {...ANIM} />
            <Bar dataKey="withDelays" name="With delays" fill="url(#grad-withdelays)" radius={[6, 6, 0, 0]} maxBarSize={28} {...ANIM} />
          </BarChart>
        </ChartCard>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <ChartCard
          title="DPD Bifurcation"
          description="Delay severity across all reported payment history. Hover a bar for the exact number of DPD instances."
          minWidth={420}
          empty={severity.every((s) => s.total === 0)}
        >
          <BarChart data={severity} layout="vertical" margin={{ left: 12, right: 36 }} barCategoryGap="34%">
            <defs>
              {severity.map((s, i) => (
                <SeriesGradient key={s.label} id={`grad-dpd-${i}`} color={s.color} />
              ))}
            </defs>
            <CartesianGrid {...GRID} horizontal={false} />
            <XAxis type="number" allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} />
            <YAxis type="category" dataKey="label" tick={AXIS} width={110} tickLine={false} axisLine={false} />
            <RTooltip cursor={CURSOR} content={<ChartTooltip valueFormat={count} />} />
            <Bar dataKey="total" name="DPD instances" radius={[0, 8, 8, 0]} maxBarSize={26} {...ANIM}>
              {severity.map((s, i) => (
                <Cell key={s.label} fill={`url(#grad-dpd-${i})`} />
              ))}
            </Bar>
          </BarChart>
        </ChartCard>

        <ChartCard
          title="All Accounts by Type"
          description="Enquiries vs approved accounts."
          series={mixSeries}
          minWidth={420}
          empty={typeMix.length === 0}
        >
          <BarChart data={typeMix} barCategoryGap="26%" barGap={4} margin={{ top: 8 }}>
            <defs>
              <SeriesGradient id="grad-enq" color="var(--chart-4)" />
              <SeriesGradient id="grad-acc" color="var(--chart-1)" />
            </defs>
            <CartesianGrid {...GRID} vertical={false} />
            <XAxis dataKey="type" tick={AXIS} tickLine={false} axisLine={false} interval={0} height={56} angle={-15} textAnchor="end" />
            <YAxis allowDecimals={false} tick={AXIS} tickLine={false} axisLine={false} width={40} />
            <RTooltip cursor={CURSOR} content={<ChartTooltip valueFormat={count} />} />
            <Bar dataKey="enquiries" name="Enquiries" fill="url(#grad-enq)" radius={[6, 6, 0, 0]} maxBarSize={28} {...ANIM} />
            <Bar dataKey="accounts" name="Approved accounts" fill="url(#grad-acc)" radius={[6, 6, 0, 0]} maxBarSize={28} {...ANIM} />
          </BarChart>
        </ChartCard>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-base">DPD Calendar Heatmap</CardTitle>
          <CardDescription>
            Total days past due reported in each month, from the first year a delay appears through
            the report year. Cell colour follows how many delay days fell in that month.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4">
          {heatRows.length === 0 ? (
            <p className="py-6 text-sm text-muted-foreground">No DPDs recorded.</p>
          ) : (
            <>
              <div className="max-h-96 overflow-auto">
                <table className="w-full min-w-[640px] text-sm">
                  <thead className="sticky top-0 z-10 bg-card">
                    <tr className="text-xs text-muted-foreground">
                      <th className="p-2 text-left font-medium">Year</th>
                      {MONTH_LABELS.map((m) => (
                        <th key={m} className="p-2 font-medium">
                          {m}
                        </th>
                      ))}
                      <th className="p-2 font-medium">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {heatRows.map((y) => (
                      <tr key={y.year}>
                        <td className="p-2 font-medium">{y.year}</td>
                        {y.months.map((c, i) => (
                          <td key={i} className="p-1 text-center">
                            <div
                              title={
                                c === 0
                                  ? `${MONTH_LABELS[i]} ${y.year}: no delay reported`
                                  : `${MONTH_LABELS[i]} ${y.year}: ${c} delay day${c > 1 ? "s" : ""}`
                              }
                              className={cn(
                                "flex h-8 cursor-default items-center justify-center rounded-md text-xs font-medium tabular-nums text-white transition-transform hover:scale-105",
                                c === 0 && "bg-muted text-muted-foreground/50",
                              )}
                              style={c === 0 ? undefined : { backgroundColor: heatColor(c) }}
                            >
                              {c === 0 ? "-" : c}
                            </div>
                          </td>
                        ))}
                        <td className="p-2 text-center font-semibold tabular-nums">{y.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                <span>Fewer delay days</span>
                {[0.2, 0.4, 0.6, 0.8, 1].map((t) => (
                  <span
                    key={t}
                    className="size-4 rounded"
                    style={{ backgroundColor: heatColor(t * maxCell) }}
                  />
                ))}
                <span>More delay days (max {maxCell} in a month)</span>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

